// Case detail with five tabs:
//
//   Documents | Search | Cross-Check | Drafts | Activity
//
// The page owns the Case identity + the tab state; each tab is its own
// component (in components/case/...) so each can manage its own data
// hooks independently.

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError, cases as casesApi } from "../lib/api";
import { useAsync } from "../hooks/useAsync";
import { pushToast } from "../components/Toast";
import { CaseDocumentsTab } from "../components/case/CaseDocumentsTab";
import { CaseSearchTab } from "../components/case/CaseSearchTab";
import { CaseCrossCheckTab } from "../components/case/CaseCrossCheckTab";
import { CaseDraftsTab } from "../components/case/CaseDraftsTab";
import { CaseActivityTab } from "../components/case/CaseActivityTab";
import { CaseDeadlinesTab } from "../components/case/CaseDeadlinesTab";

type TabKey = "documents" | "search" | "cross-check" | "drafts" | "deadlines" | "activity";

const TABS: { key: TabKey; label: string }[] = [
  { key: "documents", label: "Documents" },
  { key: "search", label: "Search" },
  { key: "cross-check", label: "Cross-Check" },
  { key: "drafts", label: "Drafts" },
  { key: "deadlines", label: "Deadlines" },
  { key: "activity", label: "Activity" },
];

export function CaseDetailPage(): JSX.Element {
  const params = useParams<{ caseId: string }>();
  const caseId = Number(params.caseId);
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>("documents");
  const { data: caseRow, loading, error, refresh } = useAsync(
    () => casesApi.get(caseId),
    [caseId],
  );

  if (Number.isNaN(caseId)) {
    return <p className="text-sm text-red-700">Invalid case id.</p>;
  }
  if (loading) return <p className="text-sm text-slate-500">Loading Case…</p>;
  if (error)
    return (
      <p className="text-sm text-red-700">
        Could not load Case: {error.message}
      </p>
    );
  if (!caseRow) return <p className="text-sm text-slate-500">No Case.</p>;

  const onArchiveToggle = async (): Promise<void> => {
    try {
      if (caseRow.archived_at) {
        await casesApi.unarchive(caseId);
        pushToast("success", "Case unarchived");
      } else {
        await casesApi.archive(caseId);
        pushToast("success", "Case archived");
      }
      refresh();
    } catch (err) {
      pushToast("error", err instanceof ApiError ? err.message : "Action failed");
    }
  };

  const onDelete = async (): Promise<void> => {
    if (!window.confirm("Delete this Case? Documents stay; only the Case + links are removed.")) {
      return;
    }
    try {
      await casesApi.remove(caseId);
      pushToast("success", "Case deleted");
      navigate("/cases");
    } catch (err) {
      pushToast("error", err instanceof ApiError ? err.message : "Delete failed");
    }
  };

  return (
    <div>
      <nav className="mb-2 text-sm text-slate-500">
        <Link to="/cases" className="hover:text-brand-700">
          Cases
        </Link>
        <span aria-hidden> / </span>
        <span>{caseRow.name}</span>
      </nav>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            {caseRow.name}
            {caseRow.archived_at && (
              <span className="ml-3 align-middle badge-yellow">archived</span>
            )}
          </h1>
          <p className="text-sm text-slate-500">
            {caseRow.case_type ? `${caseRow.case_type} · ` : ""}
            {caseRow.document_count} document{caseRow.document_count === 1 ? "" : "s"} · created{" "}
            {new Date(caseRow.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/cases/${caseId}/drafts/new`} className="btn-primary">
            New DWC-154 draft
          </Link>
          <Link to={`/cases/${caseId}/preauth/new`} className="btn-primary">
            New preauth letter
          </Link>
          <button type="button" onClick={onArchiveToggle} className="btn-secondary">
            {caseRow.archived_at ? "Unarchive" : "Archive"}
          </button>
          <button type="button" onClick={onDelete} className="btn-danger">
            Delete
          </button>
        </div>
      </div>
      {caseRow.notes && (
        <p className="mb-4 max-w-prose-narrow rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700">
          {caseRow.notes}
        </p>
      )}
      <div className="mb-6 border-b border-slate-200">
        <nav role="tablist" aria-label="Case sections" className="-mb-px flex space-x-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium ${
                tab === t.key
                  ? "border-brand-600 text-brand-700"
                  : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>
      <div>
        {tab === "documents" && (
          <CaseDocumentsTab caseId={caseId} onChanged={refresh} />
        )}
        {tab === "search" && <CaseSearchTab caseId={caseId} />}
        {tab === "cross-check" && <CaseCrossCheckTab caseId={caseId} />}
        {tab === "drafts" && <CaseDraftsTab caseId={caseId} />}
        {tab === "deadlines" && <CaseDeadlinesTab caseId={caseId} />}
        {tab === "activity" && <CaseActivityTab caseId={caseId} />}
      </div>
    </div>
  );
}
